import { APP_CONFIG } from "@/config/app-config";
import type { RuntimeBus } from "@/runtime/bus";
import type { RuntimeEvent } from "@/runtime/events";
import {
  countQueuedTelemetryEvents,
  deleteQueuedTelemetryEvents,
  listQueuedTelemetryEvents,
  queueTelemetryEvent,
} from "@/storage/telemetry";

import type {
  DevHarnessContext,
  DevHarnessCorrelation,
  DevHarnessEvent,
  DevHarnessKind,
  DevHarnessLevel,
  DevHarnessSnapshot,
  QueuedDevHarnessEvent,
} from "./types";

interface EmitHarnessInput {
  level?: DevHarnessLevel;
  kind: DevHarnessKind;
  event: string;
  data?: Record<string, unknown>;
  msg?: string;
}

interface RuntimeContextState {
  threadId: string | null;
  route: string | null;
  phase: string | null;
}

const DEVICE_ID_KEY = "privatelocalchat.dev-harness.device-id.v1";
const PAGE_ID_KEY = "privatelocalchat.dev-harness.page-id.v1";
const FLUSH_INTERVAL_MS = 12_000;
const BATCH_SIZE = 50;
const HARNESS_PREFIX = "/api/dev-harness/";
const NOISY_FETCH_PATH_PREFIXES = [
  "/models/",
  "/ort/",
  "/transformers-ort/",
  "/wllama/",
  "/audio-worklets/",
  "/assets/",
] as const;
const NOISY_FETCH_PATHS = new Set([
  "/fixtures/assistant.wav",
  "/healthz",
  "/tokenizer_sandbox.html",
  "/tokenizer_sandbox.js",
]);
const RUNTIME_DETAIL_MIN_INTERVAL_MS: Partial<Record<RuntimeEvent["type"], number>> = {
  "install.progress": 1000,
  "stt.partial": 800,
  "llm.delta": 800,
  "tts.audio": 2000,
};

const snapshotListeners = new Set<(snapshot: DevHarnessSnapshot) => void>();
const runtimeDetailLastEmittedAt = new Map<string, number>();
const runtimeContext: RuntimeContextState = {
  threadId: null,
  route: null,
  phase: null,
};

const state: {
  started: boolean;
  seq: number;
  deviceId: string | null;
  pageId: string | null;
  flushTimer: number | null;
  flushInFlight: boolean;
  lifecycleInstalled: boolean;
  fetchPatched: boolean;
  consolePatched: boolean;
  originalFetch: typeof fetch | null;
  lastVisibility: string | null;
  lastOnline: boolean | null;
  snapshot: DevHarnessSnapshot;
} = {
  started: false,
  seq: 0,
  deviceId: null,
  pageId: null,
  flushTimer: null,
  flushInFlight: false,
  lifecycleInstalled: false,
  fetchPatched: false,
  consolePatched: false,
  originalFetch: null,
  lastVisibility: null,
  lastOnline: null,
  snapshot: {
    enabled: APP_CONFIG.devHarnessEnabled,
    endpoint: APP_CONFIG.devHarnessEndpoint,
    queueCount: 0,
    flushInFlight: false,
    lastFlushAt: null,
    lastError: null,
    pageId: null,
    deviceId: null,
  },
};

function makeId(prefix: string): string {
  const raw =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  return `${prefix}-${raw}`;
}

function shortString(value: string, max = 160): string {
  return value.length <= max ? value : `${value.slice(0, max)}...`;
}

function messageFromUnknown(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === "string" ? error : "Unknown error";
}

function safeStorageGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Development harness must never break the app.
  }
}

function ensureDeviceId(): string {
  if (state.deviceId) {
    return state.deviceId;
  }

  if (typeof window === "undefined") {
    state.deviceId = makeId("dv");
    return state.deviceId;
  }

  const existing = safeStorageGet(window.localStorage, DEVICE_ID_KEY);

  if (existing) {
    state.deviceId = existing;
    return existing;
  }

  const next = makeId("dv");
  safeStorageSet(window.localStorage, DEVICE_ID_KEY, next);
  state.deviceId = next;
  return next;
}

function ensurePageId(): string {
  if (state.pageId) {
    return state.pageId;
  }

  if (typeof window === "undefined") {
    state.pageId = makeId("pg");
    return state.pageId;
  }

  const existing = safeStorageGet(window.sessionStorage, PAGE_ID_KEY);

  if (existing) {
    state.pageId = existing;
    return existing;
  }

  const next = makeId("pg");
  safeStorageSet(window.sessionStorage, PAGE_ID_KEY, next);
  state.pageId = next;
  return next;
}

function nextSeq(): number {
  state.seq += 1;
  return state.seq;
}

function currentOnline(): boolean | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  return navigator.onLine;
}

function currentVisibility(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  return document.visibilityState ?? null;
}

function currentStandalone(): boolean | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.matchMedia?.("(display-mode: standalone)").matches ?? null;
}

function currentUaBucket(): string | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  const ua = navigator.userAgent ?? "";

  if (ua.includes("iPhone")) {
    return "iPhone";
  }

  if (ua.includes("iPad")) {
    return "iPad";
  }

  if (ua.includes("Android")) {
    return "Android";
  }

  if (ua.includes("Mac")) {
    return "Mac";
  }

  return ua.slice(0, 40) || null;
}

function currentUrlPath(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  return `${window.location.pathname}${window.location.search}` || null;
}

function currentLanguage(): string | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  return navigator.language ?? null;
}

function buildCorrelation(): DevHarnessCorrelation {
  return {
    device_id: ensureDeviceId(),
    page_id: ensurePageId(),
    thread_id: runtimeContext.threadId,
    route: runtimeContext.route,
    phase: runtimeContext.phase,
    seq: nextSeq(),
  };
}

function buildContext(): DevHarnessContext {
  return {
    online: currentOnline(),
    visibility: currentVisibility(),
    standalone: currentStandalone(),
    ua_bucket: currentUaBucket(),
    language: currentLanguage(),
    url_path: currentUrlPath(),
  };
}

function createQueuedEvent(input: EmitHarnessInput): QueuedDevHarnessEvent {
  const timestamp = new Date().toISOString();
  return {
    id: makeId("evt"),
    queuedAt: timestamp,
    at: timestamp,
    v: 1,
    plane: "browser",
    app: "privatelocalchat",
    level: input.level ?? "info",
    kind: input.kind,
    event: input.event,
    corr: buildCorrelation(),
    ctx: buildContext(),
    data: input.data,
    msg: input.msg,
  };
}

function notifySnapshot(): void {
  state.snapshot.pageId = state.pageId;
  state.snapshot.deviceId = state.deviceId;
  snapshotListeners.forEach((listener) => listener({ ...state.snapshot }));
}

function updateSnapshot(patch: Partial<DevHarnessSnapshot>): void {
  state.snapshot = {
    ...state.snapshot,
    ...patch,
  };
  notifySnapshot();
}

async function enqueueEvent(event: QueuedDevHarnessEvent): Promise<void> {
  try {
    await queueTelemetryEvent(event);
    updateSnapshot({ queueCount: state.snapshot.queueCount + 1 });
    scheduleFlush("enqueue", getFlushDelayForEvent(event));
  } catch (error) {
    updateSnapshot({ lastError: `queue failed: ${messageFromUnknown(error)}` });
  }
}

function shouldSkipFetchTelemetry(url: URL): boolean {
  return url.pathname.startsWith(HARNESS_PREFIX);
}

function shouldSuppressSuccessfulFetchTelemetry(url: URL, method: string): boolean {
  if (method.toUpperCase() !== "GET") {
    return false;
  }

  if (NOISY_FETCH_PATHS.has(url.pathname)) {
    return true;
  }

  return NOISY_FETCH_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix));
}

function getFlushDelayForEvent(event: QueuedDevHarnessEvent): number {
  if (
    state.snapshot.queueCount >= BATCH_SIZE - 1 ||
    event.level === "error" ||
    event.level === "warning" ||
    event.kind === "incident"
  ) {
    return 250;
  }

  if (event.kind === "summary" || event.kind === "transition") {
    return 1500;
  }

  return FLUSH_INTERVAL_MS;
}

function summarizeFetchInput(
  url: URL,
  method: string,
  init?: RequestInit,
): Record<string, unknown> {
  return {
    method,
    path: url.pathname,
    search: url.search || "",
    cache: init?.cache ?? null,
    mode: init?.mode ?? null,
    credentials: init?.credentials ?? null,
  };
}

function serializeConsoleArgs(args: unknown[]): string {
  return shortString(
    args
      .map((arg) => {
        if (typeof arg === "string") {
          return arg;
        }

        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(" "),
    240,
  );
}

function summarizeRuntimeEvent(event: RuntimeEvent): Record<string, unknown> {
  switch (event.type) {
    case "install.progress":
      return {
        lane: event.lane,
        received: event.received,
        total: event.total,
      };
    case "install.ready":
      return {
        lane: event.lane,
        manifestId: event.manifestId,
      };
    case "stt.partial":
    case "stt.final":
    case "llm.delta":
      return {
        textLength: event.text.length,
        preview: shortString(event.text, 120),
      };
    case "tts.audio":
      return {
        sampleRate: event.sampleRate,
        pcmLength: event.pcm.length,
      };
    case "error":
      return {
        domain: event.domain,
        message: shortString(event.message, 160),
      };
    default:
      return {};
  }
}

function shouldEmitRuntimeDetail(event: RuntimeEvent): boolean {
  const minInterval = RUNTIME_DETAIL_MIN_INTERVAL_MS[event.type];

  if (!minInterval) {
    return true;
  }

  if (event.type === "install.progress" && event.received >= event.total) {
    return true;
  }

  const scope =
    "lane" in event && typeof event.lane === "string" ? `${event.type}:${event.lane}` : event.type;
  const now = performance.now();
  const lastAt = runtimeDetailLastEmittedAt.get(scope) ?? 0;

  if (now - lastAt < minInterval) {
    return false;
  }

  runtimeDetailLastEmittedAt.set(scope, now);
  return true;
}

async function flushQueuedEvents(reason: string): Promise<void> {
  if (!APP_CONFIG.devHarnessEnabled || state.flushInFlight) {
    return;
  }

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return;
  }

  const sender = state.originalFetch ?? fetch;
  state.flushInFlight = true;
  updateSnapshot({ flushInFlight: true });

  try {
    for (;;) {
      const batch = await listQueuedTelemetryEvents(BATCH_SIZE);

      if (!batch.length) {
        break;
      }

      const response = await sender(APP_CONFIG.devHarnessEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        keepalive: true,
        body: JSON.stringify({
          reason,
          events: batch,
        }),
      });

      if (!response.ok) {
        throw new Error(`receiver returned ${response.status}`);
      }

      const payload = (await response.json().catch(() => null)) as
        | { accepted_ids?: string[] }
        | null;
      const acceptedIds =
        payload?.accepted_ids?.length ? payload.accepted_ids : batch.map((event) => event.id);

      await deleteQueuedTelemetryEvents(acceptedIds);
      updateSnapshot({
        queueCount: Math.max(0, state.snapshot.queueCount - acceptedIds.length),
        lastFlushAt: new Date().toISOString(),
        lastError: null,
      });
    }
  } catch (error) {
    updateSnapshot({ lastError: `flush failed: ${messageFromUnknown(error)}` });
  } finally {
    state.flushInFlight = false;
    updateSnapshot({ flushInFlight: false });
  }
}

function scheduleFlush(reason: string, delayMs = FLUSH_INTERVAL_MS): void {
  if (!APP_CONFIG.devHarnessEnabled || typeof window === "undefined") {
    return;
  }

  if (state.flushTimer) {
    window.clearTimeout(state.flushTimer);
  }

  state.flushTimer = window.setTimeout(() => {
    state.flushTimer = null;
    void flushQueuedEvents(reason);
  }, delayMs);
}

function installFetchPatch(): void {
  if (state.fetchPatched || typeof window === "undefined") {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  state.originalFetch = originalFetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = input instanceof Request ? input : new Request(input, init);
    const url = new URL(request.url, window.location.origin);

    if (shouldSkipFetchTelemetry(url)) {
      return originalFetch(input, init);
    }

    const method = request.method || init?.method || "GET";
    const suppressSuccessDetail = shouldSuppressSuccessfulFetchTelemetry(url, method);
    const startedAt = performance.now();

    if (!suppressSuccessDetail) {
      emitDevHarness({
        level: "debug",
        kind: "detail",
        event: "fetch.start",
        data: summarizeFetchInput(url, method, init),
      });
    }

    try {
      const response = await originalFetch(input, init);
      const elapsedMs = Math.round(performance.now() - startedAt);

      if (!response.ok || !suppressSuccessDetail) {
        emitDevHarness({
          level: response.ok ? "debug" : "warning",
          kind: response.ok ? "detail" : "incident",
          event: response.ok ? "fetch.ok" : "fetch.response_error",
          data: {
            ...summarizeFetchInput(url, method, init),
            status: response.status,
            elapsed_ms: elapsedMs,
          },
        });
      }

      return response;
    } catch (error) {
      emitDevHarness({
        level: "warning",
        kind: "incident",
        event: "fetch.fail",
        msg: shortString(messageFromUnknown(error), 180),
        data: {
          ...summarizeFetchInput(url, method, init),
          elapsed_ms: Math.round(performance.now() - startedAt),
        },
      });
      throw error;
    }
  };

  state.fetchPatched = true;
}

function installConsolePatch(): void {
  if (state.consolePatched || typeof window === "undefined") {
    return;
  }

  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);

  console.warn = (...args: unknown[]) => {
    emitDevHarness({
      level: "warning",
      kind: "incident",
      event: "console.warn",
      msg: serializeConsoleArgs(args),
    });
    originalWarn(...args);
  };

  console.error = (...args: unknown[]) => {
    emitDevHarness({
      level: "error",
      kind: "incident",
      event: "console.error",
      msg: serializeConsoleArgs(args),
    });
    originalError(...args);
  };

  state.consolePatched = true;
}

function installLifecycleListeners(): void {
  if (state.lifecycleInstalled || typeof window === "undefined") {
    return;
  }

  state.lastVisibility = currentVisibility();
  state.lastOnline = currentOnline();

  window.addEventListener("online", () => {
    state.lastOnline = true;
    emitDevHarness({
      kind: "transition",
      event: "browser.online",
      msg: "Device reported online",
    });
    scheduleFlush("online", 50);
  });

  window.addEventListener("offline", () => {
    state.lastOnline = false;
    emitDevHarness({
      level: "warning",
      kind: "transition",
      event: "browser.offline",
      msg: "Device reported offline",
    });
  });

  document.addEventListener("visibilitychange", () => {
    const visibility = currentVisibility();

    if (visibility === state.lastVisibility) {
      return;
    }

    state.lastVisibility = visibility;
    emitDevHarness({
      kind: "transition",
      event: "browser.visibility",
      data: { visibility },
    });
    scheduleFlush("visibility", 500);
  });

  window.addEventListener("pagehide", (event) => {
    emitDevHarness({
      kind: "summary",
      event: "browser.pagehide",
      msg: "Page hidden",
      data: {
        // event.persisted=true means the page is going into the BFCache (alive,
        // can resume). false means a real teardown — useful to distinguish
        // "user navigated away" from "iOS evicted us".
        persisted: (event as PageTransitionEvent).persisted ?? null,
      },
    });
    void flushQueuedEvents("pagehide");
  });

  // Page Lifecycle v2: `freeze` fires when the UA is about to suspend the
  // page (pre-eviction on memory pressure on iOS), `resume` fires when the
  // page is reanimated. The freeze handler is the last opportunity to flush
  // before iOS may stop running our timers.
  window.addEventListener("freeze", () => {
    emitDevHarness({
      level: "warning",
      kind: "transition",
      event: "browser.freeze",
      msg: "Page frozen",
      data: collectMemorySnapshotSync(),
    });
    void flushQueuedEvents("freeze");
  });
  window.addEventListener("resume", () => {
    emitDevHarness({
      kind: "transition",
      event: "browser.resume",
      msg: "Page resumed",
      data: collectMemorySnapshotSync(),
    });
  });
  window.addEventListener("pageshow", (event) => {
    // pageshow fires on fresh load AND on BFCache restoration.
    // event.persisted=true ⇒ restored from BFCache (we lived through it).
    emitDevHarness({
      kind: "transition",
      event: "browser.pageshow",
      data: {
        persisted: (event as PageTransitionEvent).persisted ?? null,
        ...collectMemorySnapshotSync(),
      },
    });
  });

  window.addEventListener("error", (event) => {
    emitDevHarness({
      level: "error",
      kind: "incident",
      event: "window.error",
      msg: shortString(event.message || "Window error", 220),
      data: {
        file: event.filename || null,
        line: event.lineno || null,
        column: event.colno || null,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    emitDevHarness({
      level: "error",
      kind: "incident",
      event: "window.unhandledrejection",
      msg: shortString(messageFromUnknown(event.reason), 220),
    });
  });

  installInputListeners();
  installFocusListeners();
  state.lifecycleInstalled = true;
}

// -- User-input instrumentation ---------------------------------------------
//
// Downstream action events (install.lane.start, worker.task_start, ...) are
// only emitted when a handler runs. If a tap lands but the handler never
// fires, or the page is reset between tap and handler, we get no signal.
// These listeners capture raw user input so the harness always knows whether
// a user did something, independent of whether any app code responded.
//
// Intentionally noisy within reason: mobile PWA sessions issue only a handful
// of taps per session, so per-event cost is tiny, and the signal of a missing
// tap is diagnostic.

const INPUT_TARGET_TAGS = new Set(["BUTTON", "A", "INPUT", "TEXTAREA", "SELECT", "LABEL"]);
const INPUT_EVENT_MIN_INTERVAL_MS = 30;
let lastInputEmitAt = 0;

function describeEventTarget(target: EventTarget | null): Record<string, unknown> {
  if (!(target instanceof Element)) {
    return { target_kind: target ? "non-element" : "none" };
  }

  // Walk up to the closest actionable ancestor so "tap on the <span> inside a
  // <button>" reports the button's identity, not the span's.
  let actionable: Element | null = target;
  while (actionable && !INPUT_TARGET_TAGS.has(actionable.tagName) && !actionable.getAttribute("data-bench-id")) {
    actionable = actionable.parentElement;
  }
  const element = actionable ?? target;

  const label =
    element.getAttribute?.("aria-label") ||
    element.getAttribute?.("data-bench-id") ||
    (element instanceof HTMLElement ? (element.innerText ?? "").trim() : "") ||
    "";
  const id = element.id || null;
  const classes = element.className && typeof element.className === "string"
    ? element.className.split(/\s+/).filter(Boolean).slice(0, 5)
    : [];

  return {
    target_tag: element.tagName || null,
    target_id: id,
    target_classes: classes,
    target_label: label ? shortString(label, 80) : null,
    is_actionable: INPUT_TARGET_TAGS.has(element.tagName),
  };
}

function installInputListeners(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  // Capture-phase so the listener sees the event even if a handler calls
  // stopPropagation.
  document.addEventListener(
    "pointerdown",
    (event) => {
      const now = performance.now();
      if (now - lastInputEmitAt < INPUT_EVENT_MIN_INTERVAL_MS) {
        return;
      }
      lastInputEmitAt = now;
      const descriptor = describeEventTarget(event.target);
      emitDevHarness({
        kind: "transition",
        event: "ui.pointerdown",
        data: {
          ...descriptor,
          pointer_type: event.pointerType,
          client_x: Math.round(event.clientX),
          client_y: Math.round(event.clientY),
          button: event.button,
        },
      });
    },
    { capture: true, passive: true },
  );

  // Click is higher-semantic than pointerdown — it fires only after a real
  // gesture completion and filters out scroll-interrupted touches. We keep
  // both so pointerdown-without-click tells us "user touched but scrolled".
  document.addEventListener(
    "click",
    (event) => {
      const descriptor = describeEventTarget(event.target);
      emitDevHarness({
        kind: "transition",
        event: "ui.click",
        data: {
          ...descriptor,
          client_x: Math.round(event.clientX),
          client_y: Math.round(event.clientY),
        },
      });
    },
    { capture: true, passive: true },
  );

  // Composer keystrokes — coarse-grained (character count, not content) so
  // we know whether the user was typing right before a reset.
  document.addEventListener(
    "input",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)) {
        return;
      }
      const now = performance.now();
      if (now - lastInputEmitAt < INPUT_EVENT_MIN_INTERVAL_MS) {
        return;
      }
      lastInputEmitAt = now;
      emitDevHarness({
        kind: "detail",
        event: "ui.input",
        data: {
          target_tag: target.tagName,
          target_id: target.id || null,
          value_length: target.value.length,
        },
      });
    },
    { capture: true, passive: true },
  );
}

// -- Memory + lifecycle snapshot helpers ------------------------------------
//
// iOS Safari does not expose `performance.memory`, but it does expose enough
// timing/lifecycle signal that a series of these in the post-smoke window
// will tell us whether the page was visible / online / in a BFCache-eligible
// state right before iOS evicted it. On Chromium we additionally get JS heap
// stats. `measureUserAgentSpecificMemory()` is async + COOP/COEP-gated, so
// it gets its own async snapshot helper.

interface PerformanceMemoryShape {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
}

function collectMemorySnapshotSync(): Record<string, unknown> {
  const perfWithMemory =
    typeof performance !== "undefined"
      ? (performance as Performance & { memory?: PerformanceMemoryShape })
      : null;
  const mem = perfWithMemory?.memory;
  return {
    // Wall-clock + perf timeline so the receiver can measure deltas across
    // events even if events arrive batched out of order.
    wall_time_ms: typeof Date !== "undefined" ? Date.now() : null,
    perf_now_ms:
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? Math.round(performance.now())
        : null,
    visibility: currentVisibility(),
    online: currentOnline(),
    standalone: currentStandalone(),
    // Chromium-only. iOS will report nulls; that's expected and itself
    // diagnostic (presence of `usedJSHeapSize` proves we're on Chromium).
    used_js_heap_bytes: typeof mem?.usedJSHeapSize === "number" ? mem.usedJSHeapSize : null,
    total_js_heap_bytes: typeof mem?.totalJSHeapSize === "number" ? mem.totalJSHeapSize : null,
    js_heap_size_limit_bytes:
      typeof mem?.jsHeapSizeLimit === "number" ? mem.jsHeapSizeLimit : null,
  };
}

async function collectMemorySnapshotAsync(): Promise<Record<string, unknown>> {
  const sync = collectMemorySnapshotSync();
  const perfWithMeasure =
    typeof performance !== "undefined"
      ? (performance as Performance & {
          measureUserAgentSpecificMemory?: () => Promise<{ bytes?: number }>;
        })
      : null;
  // measureUserAgentSpecificMemory is the canonical "process memory" API on
  // Chromium with COOP/COEP. iOS doesn't have it. The estimate() field tells
  // us how the UA accounts for OPFS — paired with the heap fields we can
  // see whether process pressure or OPFS pressure dominates.
  let uaMemoryBytes: number | null = null;
  if (typeof perfWithMeasure?.measureUserAgentSpecificMemory === "function") {
    try {
      const result = await perfWithMeasure.measureUserAgentSpecificMemory();
      if (typeof result?.bytes === "number") uaMemoryBytes = result.bytes;
    } catch {
      // Cross-origin isolation not present, or API rejected. Both diagnostic.
    }
  }
  let opfsUsage: number | null = null;
  let opfsQuota: number | null = null;
  let usageDetails: Record<string, unknown> | null = null;
  if (
    typeof navigator !== "undefined" &&
    navigator.storage &&
    typeof navigator.storage.estimate === "function"
  ) {
    try {
      // usageDetails (Chromium) breaks down `usage` by storage type:
      // {indexedDB, caches, fileSystem, serviceWorkerRegistrations, ...}.
      // iOS Safari does not provide it (we'll see null). Either way the
      // breakdown tells us if non-OPFS storage is silently growing.
      const estimate = (await navigator.storage.estimate()) as StorageEstimate & {
        usageDetails?: Record<string, number>;
      };
      opfsUsage = estimate.usage ?? null;
      opfsQuota = estimate.quota ?? null;
      usageDetails = estimate.usageDetails ?? null;
    } catch {
      // Reported nulls; non-fatal.
    }
  }
  // Connection: hint at network type and recent throughput. iOS Safari does
  // not implement it (null), Chromium/Android does. Sometimes useful to know
  // if the page was on cellular vs wifi at the eviction moment.
  const navWithConn = typeof navigator !== "undefined"
    ? (navigator as Navigator & { connection?: {
        type?: string; effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean;
      } })
    : null;
  const conn = navWithConn?.connection ?? null;
  // userActivation is what browsers use to gate sticky/transient permissions.
  // If iOS evicts on "no activation in N seconds", this lets us correlate.
  const navWithActivation = typeof navigator !== "undefined"
    ? (navigator as Navigator & { userActivation?: { isActive?: boolean; hasBeenActive?: boolean } })
    : null;
  const activation = navWithActivation?.userActivation ?? null;
  // Document focus: a tab can be `visible` but not focused (e.g., another
  // window over it on desktop, or another app interacting on iOS). This
  // surfaces the difference.
  const docHasFocus = typeof document !== "undefined" && typeof document.hasFocus === "function"
    ? safeHasFocus()
    : null;
  // Screen orientation: gives a coarse signal whether the user rotated the
  // device (sometimes coupled with iOS reflows).
  const orientation = typeof screen !== "undefined" && (screen as Screen & { orientation?: { type?: string; angle?: number } }).orientation
    ? {
        type: (screen as Screen & { orientation?: { type?: string } }).orientation?.type ?? null,
        angle: (screen as Screen & { orientation?: { angle?: number } }).orientation?.angle ?? null,
      }
    : null;
  return {
    ...sync,
    ua_memory_bytes: uaMemoryBytes,
    opfs_usage_bytes: opfsUsage,
    opfs_quota_bytes: opfsQuota,
    usage_details: usageDetails,
    document_has_focus: docHasFocus,
    user_activation_active: activation?.isActive ?? null,
    user_activation_has_been: activation?.hasBeenActive ?? null,
    connection_type: conn?.type ?? null,
    connection_effective_type: conn?.effectiveType ?? null,
    connection_downlink: conn?.downlink ?? null,
    connection_rtt: conn?.rtt ?? null,
    connection_save_data: conn?.saveData ?? null,
    orientation_type: orientation?.type ?? null,
    orientation_angle: orientation?.angle ?? null,
  };
}

function safeHasFocus(): boolean | null {
  try { return document.hasFocus(); } catch { return null; }
}

// -- requestAnimationFrame heartbeat -----------------------------------------
//
// rAF is the most sensitive throttling indicator we can observe from JS. iOS
// will throttle background tabs and pre-eviction tabs to 1 Hz or even 0 Hz
// well before any documented lifecycle event fires. We schedule a permanent
// rAF loop that just increments a counter and stamps the wall clock; per-tick
// telemetry reports the delta since the previous tick. Healthy: ~60 frames/s
// (so ~60 increments per per-second tick). Throttled: 1–2. Frozen: 0.

let rafCount = 0;
let rafLastWallTime = 0;
let rafInstalled = false;

function ensureRafHeartbeat(): void {
  if (rafInstalled || typeof window === "undefined" || typeof requestAnimationFrame !== "function") {
    return;
  }
  rafInstalled = true;
  const tick = (now: number) => {
    rafCount += 1;
    rafLastWallTime = now;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function snapshotRafSinceLast(prevCount: number): { count_now: number; delta_since_prev: number; last_wall_time_ms: number } {
  return {
    count_now: rafCount,
    delta_since_prev: rafCount - prevCount,
    last_wall_time_ms: Math.round(rafLastWallTime),
  };
}

// -- Long task observer ------------------------------------------------------
//
// PerformanceObserver "longtask" entries fire for any main-thread task >50 ms.
// Bursts of long tasks are exactly the kind of "page is unresponsive" signal
// that iOS uses as input to its eviction heuristic. We accumulate counts +
// total duration since last reset, and report deltas per tick.

let longTaskCount = 0;
let longTaskTotalDurationMs = 0;
let longTaskMaxDurationMs = 0;
let longTaskObserverInstalled = false;

function ensureLongTaskObserver(): void {
  if (longTaskObserverInstalled || typeof PerformanceObserver === "undefined") return;
  try {
    const obs = new PerformanceObserver((entries) => {
      for (const entry of entries.getEntries()) {
        longTaskCount += 1;
        longTaskTotalDurationMs += entry.duration;
        if (entry.duration > longTaskMaxDurationMs) longTaskMaxDurationMs = entry.duration;
      }
    });
    obs.observe({ type: "longtask", buffered: true });
    longTaskObserverInstalled = true;
  } catch {
    // Safari historically did not implement longtask. Diagnostic by absence:
    // we still report the counters as zero and that itself is a signal.
  }
}

function snapshotLongTasks(): Record<string, unknown> {
  return {
    long_task_count: longTaskCount,
    long_task_total_duration_ms: Math.round(longTaskTotalDurationMs),
    long_task_max_duration_ms: Math.round(longTaskMaxDurationMs),
  };
}

// -- Permissions snapshot ---------------------------------------------------
//
// We mostly care about persistent-storage. iOS revoking persistence between
// smoke and eviction would be a smoking gun.

const PERMISSIONS_TO_QUERY: PermissionName[] = [
  "persistent-storage" as PermissionName,
  "geolocation",
  "notifications",
  "microphone" as PermissionName,
  "screen-wake-lock" as PermissionName,
];

async function collectPermissionsSnapshotAsync(): Promise<Record<string, string | null>> {
  const out: Record<string, string | null> = {};
  if (typeof navigator === "undefined" || !navigator.permissions?.query) return out;
  for (const name of PERMISSIONS_TO_QUERY) {
    try {
      const status = await navigator.permissions.query({ name });
      out[String(name)] = status.state;
    } catch {
      out[String(name)] = "unsupported";
    }
  }
  return out;
}

// -- Battery snapshot --------------------------------------------------------
//
// Some iOS Safari versions implement Battery Status partially. Low battery
// can change UA's eviction heuristics — Apple has historically been more
// aggressive about reclaiming memory under battery pressure.

interface BatteryManagerLike {
  level?: number;
  charging?: boolean;
  chargingTime?: number;
  dischargingTime?: number;
}

async function collectBatterySnapshotAsync(): Promise<Record<string, unknown> | null> {
  const navWithBattery = typeof navigator !== "undefined"
    ? (navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> })
    : null;
  if (!navWithBattery?.getBattery) return null;
  try {
    const battery = await navWithBattery.getBattery();
    return {
      level: typeof battery.level === "number" ? battery.level : null,
      charging: typeof battery.charging === "boolean" ? battery.charging : null,
      charging_time_s: typeof battery.chargingTime === "number" ? battery.chargingTime : null,
      discharging_time_s: typeof battery.dischargingTime === "number" ? battery.dischargingTime : null,
    };
  } catch {
    return null;
  }
}

// Deep storage breakdown — heavier than the per-tick snapshot, intended to
// run once at the start of the watchdog so we have a baseline of what's in
// each storage bucket and how big each one is.
async function collectFullStorageBreakdownAsync(): Promise<Record<string, unknown>> {
  const cachesInfo: Array<Record<string, unknown>> = [];
  if (typeof caches !== "undefined" && caches?.keys) {
    try {
      const names = await caches.keys();
      for (const name of names) {
        try {
          const cache = await caches.open(name);
          const requests = await cache.keys();
          let bytes = 0;
          let withLength = 0;
          // Limit to first 50 entries per cache so large caches don't stall
          // the watchdog. We still report full request count.
          const sample = requests.slice(0, 50);
          for (const req of sample) {
            try {
              const res = await cache.match(req);
              const cl = res?.headers?.get("content-length");
              if (cl) {
                const n = Number(cl);
                if (Number.isFinite(n)) {
                  bytes += n;
                  withLength += 1;
                }
              }
            } catch { /* skip */ }
          }
          cachesInfo.push({
            name,
            request_count: requests.length,
            sampled: sample.length,
            sampled_with_content_length: withLength,
            sampled_bytes: bytes,
            sample_urls: sample.slice(0, 5).map((r) => {
              try { return new URL(r.url).pathname; } catch { return null; }
            }),
          });
        } catch (err) {
          cachesInfo.push({ name, error: messageFromUnknown(err) });
        }
      }
    } catch (err) {
      cachesInfo.push({ enumerate_error: messageFromUnknown(err) });
    }
  }

  let idbDatabases: Array<{ name: string | null; version: number | null }> | null = null;
  const idbWithList = typeof indexedDB !== "undefined"
    ? (indexedDB as IDBFactory & { databases?: () => Promise<Array<{ name?: string; version?: number }>> })
    : null;
  if (idbWithList?.databases) {
    try {
      const list = await idbWithList.databases();
      idbDatabases = list.map((db) => ({ name: db.name ?? null, version: db.version ?? null }));
    } catch (err) {
      idbDatabases = [{ name: `(error: ${messageFromUnknown(err)})`, version: null }];
    }
  }

  let serviceWorkerRegistrations: Array<Record<string, unknown>> | null = null;
  if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      serviceWorkerRegistrations = regs.map((reg) => ({
        scope: reg.scope ?? null,
        state_active: reg.active?.state ?? null,
        state_installing: reg.installing?.state ?? null,
        state_waiting: reg.waiting?.state ?? null,
        update_via_cache: reg.updateViaCache ?? null,
      }));
    } catch (err) {
      serviceWorkerRegistrations = [{ error: messageFromUnknown(err) }];
    }
  }

  // Persistent storage permission state — if iOS revoked persistence between
  // smoke and now, it changes its eviction calculus dramatically.
  let persisted: boolean | null = null;
  if (typeof navigator !== "undefined" && navigator.storage?.persisted) {
    try { persisted = await navigator.storage.persisted(); } catch { /* null */ }
  }
  return {
    caches: cachesInfo,
    indexed_db_databases: idbDatabases,
    service_worker_registrations: serviceWorkerRegistrations,
    persisted_at_baseline: persisted,
  };
}

// -- Watchdog diagnostic worker ---------------------------------------------
//
// Spawn a tiny inline worker on watchdog start that posts a heartbeat every
// 2 s with its own perf_now and storage.estimate(). If main-thread heartbeats
// stop at T0 and worker heartbeats stop at T1 (likely T0 ≈ T1, since iOS
// evicts the WebContent process atomically), we have an independent confirm
// of the death moment from a separate execution context. If they DIVERGE,
// iOS is doing something asymmetric (killing main thread but not workers,
// or vice versa) — that would itself be a major finding.

let watchdogWorker: Worker | null = null;
let watchdogWorkerUrl: string | null = null;

function startWatchdogWorker(generation: number): void {
  if (typeof Worker === "undefined") return;
  // Tear down any previous instance.
  stopWatchdogWorker();
  const src = `
    let seq = 0;
    const startedAt = self.performance.now();
    async function tick() {
      seq += 1;
      let usage = null, quota = null;
      try {
        if (self.navigator?.storage?.estimate) {
          const est = await self.navigator.storage.estimate();
          usage = est.usage ?? null;
          quota = est.quota ?? null;
        }
      } catch {}
      self.postMessage({
        seq,
        perf_now_ms: Math.round(self.performance.now()),
        elapsed_since_start_ms: Math.round(self.performance.now() - startedAt),
        wall_time_ms: Date.now(),
        opfs_usage_bytes: usage,
        opfs_quota_bytes: quota,
      });
    }
    self.setInterval(tick, 2000);
    tick();
  `;
  watchdogWorkerUrl = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
  try {
    watchdogWorker = new Worker(watchdogWorkerUrl);
    watchdogWorker.onmessage = (event) => {
      // Each worker tick lands as its own harness event. Worker death will
      // simply stop these from arriving — combined with the main-thread tick
      // stream, the gap between the last worker tick and last main-thread
      // tick is the diagnostic.
      emitDevHarness({
        kind: "summary",
        event: "lifecycle.post_smoke.worker_tick",
        data: {
          generation,
          ...event.data,
        },
      });
      void flushQueuedEvents("post-smoke-worker-tick");
    };
    watchdogWorker.onerror = (err) => {
      emitDevHarness({
        level: "warning",
        kind: "incident",
        event: "lifecycle.post_smoke.worker_error",
        msg: err.message || "watchdog worker error",
        data: { generation, file: err.filename, line: err.lineno },
      });
    };
  } catch (err) {
    emitDevHarness({
      level: "warning",
      kind: "incident",
      event: "lifecycle.post_smoke.worker_spawn_failed",
      msg: messageFromUnknown(err),
      data: { generation },
    });
  }
}

function stopWatchdogWorker(): void {
  if (watchdogWorker) {
    try { watchdogWorker.terminate(); } catch { /* nop */ }
    watchdogWorker = null;
  }
  if (watchdogWorkerUrl) {
    try { URL.revokeObjectURL(watchdogWorkerUrl); } catch { /* nop */ }
    watchdogWorkerUrl = null;
  }
}

// -- Wake lock attempt ------------------------------------------------------
//
// iOS Safari supports the screen wake lock API in some versions. Acquiring a
// wake lock keeps the screen on AND signals "this page wants to keep
// running" to the UA. If iOS still evicts a tab that holds an active screen
// wake lock, that's a strong signal the eviction is memory-driven, not
// idle-driven. If the lock acquisition itself is denied, that's also
// diagnostic — iOS may not allow it from a non-fullscreen page.

interface WakeLockSentinelLike {
  released?: boolean;
  release: () => Promise<void>;
  addEventListener: (type: string, cb: () => void) => void;
}
let watchdogWakeLock: WakeLockSentinelLike | null = null;

async function acquireWatchdogWakeLock(generation: number): Promise<void> {
  const navWithLock = typeof navigator !== "undefined"
    ? (navigator as Navigator & { wakeLock?: { request: (type: string) => Promise<WakeLockSentinelLike> } })
    : null;
  if (!navWithLock?.wakeLock?.request) {
    emitDevHarness({
      kind: "summary",
      event: "lifecycle.post_smoke.wake_lock_unsupported",
      data: { generation },
    });
    return;
  }
  try {
    const sentinel = await navWithLock.wakeLock.request("screen");
    watchdogWakeLock = sentinel;
    sentinel.addEventListener("release", () => {
      emitDevHarness({
        kind: "transition",
        event: "lifecycle.post_smoke.wake_lock_released",
        data: { generation },
      });
    });
    emitDevHarness({
      kind: "summary",
      event: "lifecycle.post_smoke.wake_lock_acquired",
      data: { generation },
    });
  } catch (err) {
    emitDevHarness({
      level: "warning",
      kind: "incident",
      event: "lifecycle.post_smoke.wake_lock_failed",
      msg: messageFromUnknown(err),
      data: { generation },
    });
  }
}

async function releaseWatchdogWakeLock(): Promise<void> {
  if (!watchdogWakeLock) return;
  try { await watchdogWakeLock.release(); } catch { /* nop */ }
  watchdogWakeLock = null;
}

// -- Post-smoke watchdog -----------------------------------------------------
//
// Hypothesis-free probe of the death window. After `smoke.lane.ok` the iPhone
// reloads itself ~1.6–2.8 seconds later with no error event firing. The 5 s
// OPFS heartbeat is too coarse to land inside that window. This series fires
// at fixed offsets after the trigger and force-flushes the harness queue
// after each tick so events land on the receiver before any death.
//
// The first tick at +250 ms catches "is the page even alive past the synchronous
// smoke completion." The +5000 ms tick catches "did we survive the iOS GC
// pressure window after worker.terminate." The full series at
// 250/750/1500/3000/5000/10000 ms triangulates the eviction time within ~500 ms.
//
// Cancelling: a new watchdog start cancels the previous one — we don't run
// multiple overlapping series.

// Sub-second resolution at the head where the previous death class lived
// (1.6–2.8 s), then 1-second cadence for 60 s to bracket the new ~30 s
// eviction window with ±1 s precision. Total: 62 ticks.
const POST_SMOKE_OFFSETS_MS: number[] = (() => {
  const offsets: number[] = [250, 750];
  for (let s = 1; s <= 60; s += 1) offsets.push(s * 1000);
  return offsets;
})();
let postSmokeTimers: number[] = [];
let postSmokeGen = 0;

function clearPostSmokeWatchdog(): void {
  if (typeof window === "undefined") return;
  for (const id of postSmokeTimers) window.clearTimeout(id);
  postSmokeTimers = [];
  stopWatchdogWorker();
  void releaseWatchdogWakeLock();
}

export function startPostSmokeWatchdog(reason: string): void {
  if (!APP_CONFIG.devHarnessEnabled || typeof window === "undefined") return;
  clearPostSmokeWatchdog();
  postSmokeGen += 1;
  const generation = postSmokeGen;
  const startedAt = performance.now();
  // Anchor event so the receiver sees t=0 and can compute deltas to each tick
  // even if some ticks are dropped. Sync snapshot only — async-deep stuff
  // happens just below to avoid blocking.
  emitDevHarness({
    kind: "summary",
    event: "lifecycle.post_smoke.start",
    data: {
      reason,
      generation,
      offsets_ms: [...POST_SMOKE_OFFSETS_MS],
      ...collectMemorySnapshotSync(),
    },
  });
  void flushQueuedEvents("post-smoke-anchor");

  // Side effects that run independently of the tick stream: deep storage
  // baseline, diagnostic worker, screen wake lock. None of these block
  // anything; failures are reported as their own events.
  void (async () => {
    const breakdown = await collectFullStorageBreakdownAsync();
    const permissions = await collectPermissionsSnapshotAsync();
    const battery = await collectBatterySnapshotAsync();
    emitDevHarness({
      kind: "summary",
      event: "storage.full_breakdown",
      data: { reason, generation, permissions, battery, ...breakdown },
    });
    void flushQueuedEvents("storage-breakdown");
  })();
  startWatchdogWorker(generation);
  void acquireWatchdogWakeLock(generation);
  // rAF heartbeat + long-task observer are idempotent — safe to call on
  // every watchdog start. They keep accumulating across smokes; tick payload
  // reports deltas-since-prev-tick so cumulative is fine.
  ensureRafHeartbeat();
  ensureLongTaskObserver();
  let prevRafCount = rafCount;

  for (const offsetMs of POST_SMOKE_OFFSETS_MS) {
    const timerId = window.setTimeout(async () => {
      // If a newer watchdog has started, this tick is from a stale series.
      if (generation !== postSmokeGen) return;
      const snapshot = await collectMemorySnapshotAsync();
      const rafSnap = snapshotRafSinceLast(prevRafCount);
      prevRafCount = rafSnap.count_now;
      const longTasks = snapshotLongTasks();
      emitDevHarness({
        kind: "summary",
        event: "lifecycle.post_smoke.tick",
        data: {
          reason,
          generation,
          offset_ms: offsetMs,
          actual_elapsed_ms: Math.round(performance.now() - startedAt),
          raf_count_now: rafSnap.count_now,
          raf_delta_since_prev: rafSnap.delta_since_prev,
          raf_last_wall_time_ms: rafSnap.last_wall_time_ms,
          ...longTasks,
          ...snapshot,
        },
      });
      // Aggressive flush: every tick. Each event is small, and the cost of
      // missing the last event before death is high.
      void flushQueuedEvents(`post-smoke-${offsetMs}`);
    }, offsetMs);
    postSmokeTimers.push(timerId);
  }
  // After the full 60 s bracket, tear down the diagnostic worker and the
  // wake lock so they don't leak resources between smokes.
  postSmokeTimers.push(window.setTimeout(() => {
    if (generation !== postSmokeGen) return;
    stopWatchdogWorker();
    void releaseWatchdogWakeLock();
    emitDevHarness({
      kind: "summary",
      event: "lifecycle.post_smoke.complete",
      data: { reason, generation, total_offsets: POST_SMOKE_OFFSETS_MS.length },
    });
  }, POST_SMOKE_OFFSETS_MS[POST_SMOKE_OFFSETS_MS.length - 1] + 250));
}

export async function forceFlushDevHarness(reason: string): Promise<void> {
  if (!APP_CONFIG.devHarnessEnabled) return;
  await flushQueuedEvents(reason);
}

async function emitOpfsMainThreadSnapshot(reason: string): Promise<void> {
  try {
    const estimate =
      typeof navigator !== "undefined" &&
      navigator.storage &&
      typeof navigator.storage.estimate === "function"
        ? await navigator.storage.estimate().catch(() => null)
        : null;
    // Avoid importing walkOpfsRoot on the main thread path: the walk itself
    // costs real time and we want the main-thread heartbeat cheap. Workers
    // do the expensive ground-truth walk. Main thread reports UA usage only.
    emitDevHarness({
      kind: "summary",
      event: "opfs.main_thread_snapshot",
      data: {
        reason,
        opfs_usage_bytes: estimate?.usage ?? null,
        opfs_quota_bytes: estimate?.quota ?? null,
        // Same throttling/long-task counters that post-smoke ticks emit. By
        // attaching them to the 5 s heartbeat too we get continuous coverage
        // before, during, and after install — not just in the post-smoke
        // bracket. Counters are cumulative; receiver computes deltas.
        raf_count: rafCount,
        raf_last_wall_time_ms: Math.round(rafLastWallTime),
        long_task_count: longTaskCount,
        long_task_total_duration_ms: Math.round(longTaskTotalDurationMs),
        long_task_max_duration_ms: Math.round(longTaskMaxDurationMs),
        document_has_focus: safeHasFocus(),
        persisted: typeof navigator !== "undefined" && navigator.storage && typeof navigator.storage.persisted === "function"
          ? await navigator.storage.persisted().catch(() => null)
          : null,
      },
    });
  } catch (err) {
    emitDevHarness({
      level: "warning",
      kind: "incident",
      event: "opfs.main_thread_snapshot_failed",
      msg: messageFromUnknown(err),
    });
  }
}

// Two cadences for OPFS heartbeat: 5 s default (cheap idle sampler), 1 s
// during active install/smoke (so the gap between the last harness event
// and an iOS-evicted death is bounded to ≤1 s instead of ≤5 s). The fast
// cadence is enabled by `setHeartbeatActive(true)` and disabled when
// install + smoke for the current lane have both completed.
let heartbeatTimer: number | null = null;
let heartbeatFastActive = false;
const HEARTBEAT_SLOW_MS = 5_000;
const HEARTBEAT_FAST_MS = 1_000;

function startHeartbeat(intervalMs: number, reason: string): void {
  if (typeof window === "undefined") return;
  if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
  heartbeatTimer = window.setInterval(() => {
    void emitOpfsMainThreadSnapshot(reason);
  }, intervalMs);
}

function setHeartbeatFast(active: boolean): void {
  if (active === heartbeatFastActive) return;
  heartbeatFastActive = active;
  if (active) {
    startHeartbeat(HEARTBEAT_FAST_MS, "heartbeat-fast");
    emitDevHarness({
      kind: "transition",
      event: "harness.heartbeat_fast_on",
      data: { interval_ms: HEARTBEAT_FAST_MS },
    });
  } else {
    startHeartbeat(HEARTBEAT_SLOW_MS, "heartbeat");
    emitDevHarness({
      kind: "transition",
      event: "harness.heartbeat_fast_off",
      data: { interval_ms: HEARTBEAT_SLOW_MS },
    });
  }
}

function installOpfsHeartbeat(): void {
  if (typeof window === "undefined") {
    return;
  }
  // Default cadence: 5 s. Fills in the trajectory between segmented-install
  // events when we're idle. Switches to 1 s during active install/smoke so
  // the death-window resolution matches the post-smoke watchdog.
  startHeartbeat(HEARTBEAT_SLOW_MS, "heartbeat");
}

function installFocusListeners(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.addEventListener("focus", () => {
    emitDevHarness({
      kind: "transition",
      event: "browser.window_focus",
    });
  });

  window.addEventListener("blur", () => {
    emitDevHarness({
      kind: "transition",
      event: "browser.window_blur",
    });
  });
}

function bindRuntimeBus(bus: RuntimeBus): void {
  // Auto-bump the OPFS heartbeat to 1 s during active install or smoke. The
  // bus carries `install.progress` events from the orchestrator on every
  // segment; while we're seeing them, install is active. We also flip on
  // when smoke.start fires and off when smoke.ok / smoke.fail fire. A 10 s
  // idle timer relaxes back to the 5 s default so we don't burn fast
  // cadence forever after the page goes idle.
  let installIdleTimer: number | null = null;
  const ensureFast = () => {
    setHeartbeatFast(true);
    if (typeof window !== "undefined") {
      if (installIdleTimer !== null) window.clearTimeout(installIdleTimer);
      installIdleTimer = window.setTimeout(() => {
        setHeartbeatFast(false);
        installIdleTimer = null;
      }, 10_000);
    }
  };

  bus.subscribe((event) => {
    if (event.type === "install.progress" || event.type === "smoke.start") {
      ensureFast();
    } else if (event.type === "smoke.ok" || event.type === "smoke.fail") {
      // Keep fast cadence for a few more seconds after smoke completes —
      // that's the death window we most need resolution on.
      ensureFast();
    }

    if (!shouldEmitRuntimeDetail(event) && event.type !== "error") {
      return;
    }

    emitDevHarness({
      level: event.type === "error" ? "error" : "debug",
      kind: event.type === "error" ? "incident" : "detail",
      event: `runtime.${event.type}`,
      data: summarizeRuntimeEvent(event),
    });
  });
}

export function getDevHarnessSnapshot(): DevHarnessSnapshot {
  return { ...state.snapshot };
}

export function subscribeDevHarnessSnapshot(
  listener: (snapshot: DevHarnessSnapshot) => void,
): () => void {
  snapshotListeners.add(listener);
  listener(getDevHarnessSnapshot());
  return () => snapshotListeners.delete(listener);
}

export function setDevHarnessContext(context: Partial<RuntimeContextState>): void {
  if ("threadId" in context) {
    runtimeContext.threadId = context.threadId ?? null;
  }

  if ("route" in context) {
    runtimeContext.route = context.route ?? null;
  }

  if ("phase" in context) {
    runtimeContext.phase = context.phase ?? null;
  }
}

export function emitDevHarness(input: EmitHarnessInput): void {
  if (!APP_CONFIG.devHarnessEnabled) {
    return;
  }

  const event = createQueuedEvent(input);
  void enqueueEvent(event);
}

export async function initDevHarness(bus: RuntimeBus): Promise<void> {
  if (!APP_CONFIG.devHarnessEnabled || state.started) {
    return;
  }

  state.started = true;
  ensureDeviceId();
  ensurePageId();
  updateSnapshot({
    deviceId: state.deviceId,
    pageId: state.pageId,
  });

  let carryoverQueueCount: number | null = null;
  let carryoverQueueError: string | null = null;

  try {
    carryoverQueueCount = await countQueuedTelemetryEvents();
    updateSnapshot({ queueCount: carryoverQueueCount });
  } catch (error) {
    carryoverQueueError = messageFromUnknown(error);
    updateSnapshot({ lastError: `count failed: ${carryoverQueueError}` });
  }

  installFetchPatch();
  installConsolePatch();
  installLifecycleListeners();
  // Lift the rAF + longtask observers so we have continuous throttling and
  // long-task data for the entire session, not just post-smoke.
  ensureRafHeartbeat();
  ensureLongTaskObserver();
  bindRuntimeBus(bus);

  const navigatorWithMemory = typeof navigator === "undefined"
    ? null
    : (navigator as Navigator & { deviceMemory?: number });
  const deviceMemoryGb =
    typeof navigatorWithMemory?.deviceMemory === "number"
      ? navigatorWithMemory.deviceMemory
      : null;
  const hardwareConcurrency =
    typeof navigator !== "undefined" && typeof navigator.hardwareConcurrency === "number"
      ? navigator.hardwareConcurrency
      : null;

  emitDevHarness({
    kind: "summary",
    event: "harness.boot",
    msg: "Development harness active",
    data: {
      version: APP_CONFIG.version,
      buildDate: APP_CONFIG.buildDate,
      domain: APP_CONFIG.domain,
      standalone: currentStandalone(),
      carryover_queue_count: carryoverQueueCount,
      carryover_queue_error: carryoverQueueError,
      device_memory_gb: deviceMemoryGb,
      hardware_concurrency: hardwareConcurrency,
    },
  });

  // Baseline OPFS snapshot from the main thread. Paired with the worker's
  // `opfs.worker_boot_snapshot` so we can compare what the two contexts see
  // at session start. If the main-thread snapshot agrees with the worker's,
  // the accounting is consistent across contexts. If they diverge, something
  // is weird about per-context OPFS visibility.
  void emitOpfsMainThreadSnapshot("boot");
  installOpfsHeartbeat();

  scheduleFlush("startup", 500);

  if (typeof window !== "undefined") {
    window.setInterval(() => {
      void flushQueuedEvents("interval");
    }, FLUSH_INTERVAL_MS);
  }
}
